import React, { useEffect, useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import * as dashboardApi from '../../../api/dashboardApi';
import * as adminApi from '../../../api/adminApi';
import { BRAND_BLUE_SOFT, BRAND_ORANGE, BRAND_ORANGE_DEEP, BRAND_ORANGE_LIGHT } from '../../../design-system/chartColors';

const countryTag = (country: string) => {
    const lower = country.toLowerCase();
    if (lower === 'india') return { bg: '#ecfdf3', color: '#027a48' };
    if (lower === 'usa' || lower === 'us') return { bg: '#e0eaff', color: 'var(--ds-link)' };
    if (lower === 'uk') return { bg: '#e0eaff', color: 'var(--ds-link)' };
    if (lower === 'eu') return { bg: '#f0f5ff', color: 'var(--ds-link)' };
    if (lower === 'middle east') return { bg: '#f0f5ff', color: '#f04438' };
    return { bg: '#f0f5ff', color: '#f04438' };
};

const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ACCENT = BRAND_ORANGE_LIGHT;
const ACCENT_LIGHT = '#f0f5ff';
const WEEKLY_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Gradient placeholder colors for cards without images
const PLACEHOLDER_GRADIENTS = [
    BRAND_BLUE_SOFT,
    '#c7d7fe',
    BRAND_BLUE_SOFT,
    '#12b76a',
    '#f97066',
    BRAND_BLUE_SOFT,
    '#c7d7fe',
    '#c7d7fe',
];

const PLACEHOLDER_ICONS = [
    // Newspaper
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2z"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8z"/></svg>,
    // Globe
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
    // Trending up
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
    // Shield (policy/regulation)
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    // Cpu / AI chip
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="6" height="6"/><rect x="2" y="2" width="20" height="20" rx="2"/><line x1="9" y1="2" x2="9" y2="6"/><line x1="15" y1="2" x2="15" y2="6"/><line x1="9" y1="18" x2="9" y2="22"/><line x1="15" y1="18" x2="15" y2="22"/><line x1="2" y1="9" x2="6" y2="9"/><line x1="2" y1="15" x2="6" y2="15"/><line x1="18" y1="9" x2="22" y2="9"/><line x1="18" y1="15" x2="22" y2="15"/></svg>,
    // Book (law/policy doc)
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
    // Broadcast / signal
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 6c0 0 4-2 11-2s11 2 11 2"/><path d="M3.5 10.5S6 9 12 9s8.5 1.5 8.5 1.5"/><path d="M6 15s2-1 6-1 6 1 6 1"/><circle cx="12" cy="19" r="1" fill="rgba(255,255,255,0.55)"/></svg>,
    // Bar chart
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>,
];

const getInitials = (name: string) => {
    if (!name) return 'N';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

// SRS #31: media kinds
const KIND_LABEL: Record<string, string> = { article: 'Articles', podcast: 'Podcasts', video: 'Video', linkedin: 'LinkedIn' };
const KIND_ICON: Record<string, string> = { article: '📰', podcast: '🎧', video: '▶', linkedin: 'in' };

const AVATAR_COLORS = [BRAND_ORANGE_LIGHT, BRAND_ORANGE, BRAND_ORANGE_LIGHT, '#027a48', BRAND_ORANGE, '#f04438'];
const getAvatarColor = (name: string) => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

/** Standard blog card — vertical */
const BlogCard: React.FC<{
    a: dashboardApi.NewsletterArticle;
    isDark: boolean;
    isBookmarked: boolean;
    onBookmarkToggle: (url: string) => void;
    placeholderIdx: number;
    repeatedImageUrls: Set<string>;
    /** Admins only: remove an off-topic article from the feed. */
    onDelete?: (a: dashboardApi.NewsletterArticle) => void;
}> = ({ a, isDark, isBookmarked, onBookmarkToggle, placeholderIdx, repeatedImageUrls, onDelete }) => {
    const [imgFailed, setImgFailed] = useState(false);
    const [hovered, setHovered] = useState(false);
    const isGenericImage = !!(a.image_url) && repeatedImageUrls.has(a.image_url);
    const hasImage = !!(a.image_url) && !imgFailed && !isGenericImage;
    const tag = a.country ? countryTag(a.country) : null;

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                borderRadius: 14, overflow: 'hidden',
                background: isDark ? '#101828' : 'var(--ds-surface)',
                boxShadow: hovered
                    ? (isDark ? '0 12px 36px rgba(0,0,0,0.55)' : '0 12px 36px rgba(16,24,40,0.14)')
                    : (isDark ? '0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(16,24,40,0.06)'),
                transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
                transition: 'box-shadow 0.22s, transform 0.22s',
                display: 'flex', flexDirection: 'column',
            }}
        >
            {/* Card image */}
            <a href={a.url} target="_blank" rel="noreferrer" style={{ display: 'block', position: 'relative', height: 190, overflow: 'hidden', flexShrink: 0, textDecoration: 'none' }}>
                {hasImage ? (
                    <img
                        src={a.image_url!}
                        alt={a.title}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={() => setImgFailed(true)}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: hovered ? 'scale(1.04)' : 'scale(1)', transition: 'transform 0.35s' }}
                    />
                ) : (
                    <div style={{ width: '100%', height: '100%', background: PLACEHOLDER_GRADIENTS[placeholderIdx % PLACEHOLDER_GRADIENTS.length], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {PLACEHOLDER_ICONS[placeholderIdx % PLACEHOLDER_ICONS.length]}
                    </div>
                )}

                {/* Bookmark button over image */}
                <button
                    onClick={(e) => { e.preventDefault(); onBookmarkToggle(a.url); }}
                    style={{ position: 'absolute', top: 10, right: 10, padding: '6px', background: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.88)', border: 'none', cursor: 'pointer', borderRadius: 8, display: 'flex', backdropFilter: 'blur(6px)' }}
                    title={isBookmarked ? 'Remove bookmark' : 'Bookmark article'}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={isBookmarked ? ACCENT : 'none'} stroke={isBookmarked ? ACCENT : (isDark ? '#98a2b3' : '#667085')} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                    </svg>
                </button>

                {/* Admin-only: delete a noisy/off-topic article from the feed */}
                {onDelete && (
                    <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(a); }}
                        style={{ position: 'absolute', top: 10, right: 48, padding: '6px', background: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.88)', border: 'none', cursor: 'pointer', borderRadius: 8, display: 'flex', backdropFilter: 'blur(6px)' }}
                        title="Remove this article from the feed (admin)"
                        aria-label="Delete article"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d92d20" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                        </svg>
                    </button>
                )}
            </a>

            {/* Card body */}
            <div style={{ padding: '18px 20px 20px 20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                {/* Category/country chip */}
                <div style={{ marginBottom: 10 }}>
                    {tag && a.country ? (
                        <span style={{ background: isDark ? 'rgba(97, 114, 243, 0.15)' : ACCENT_LIGHT, color: isDark ? '#c7d7fe' : ACCENT, fontSize: '0.68rem', fontWeight: 800, padding: '3px 10px', borderRadius: 999, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            {a.country}
                        </span>
                    ) : a.source_name ? (
                        <span style={{ background: isDark ? 'rgba(97, 114, 243, 0.15)' : ACCENT_LIGHT, color: isDark ? '#c7d7fe' : ACCENT, fontSize: '0.68rem', fontWeight: 800, padding: '3px 10px', borderRadius: 999, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            {a.source_name}
                        </span>
                    ) : null}
                </div>

                {/* Title */}
                <a href={a.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', flex: 1 }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: 700, lineHeight: 1.45, color: hovered ? ACCENT : (isDark ? '#f2f4f7' : '#101828'), transition: 'color 0.18s', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                        {a.title}
                    </h3>
                </a>
                {/* SRS #31: media badge + share */}
                {(a.media_kind && a.media_kind !== 'article') || a.share ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        {a.media_kind && a.media_kind !== 'article' && (
                            <span data-testid="media-badge" style={{ fontSize: '0.66rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: isDark ? 'rgba(97,114,243,0.18)' : '#eef2ff', color: isDark ? '#c7d7fe' : BRAND_ORANGE_DEEP }}>
                                {KIND_ICON[a.media_kind]} {KIND_LABEL[a.media_kind]}
                            </span>
                        )}
                        <span style={{ flex: 1 }} />
                        {a.share?.linkedin && (
                            <a href={a.share.linkedin} target="_blank" rel="noreferrer" aria-label="Share on LinkedIn" title="Share on LinkedIn"
                               style={{ fontSize: '0.7rem', fontWeight: 800, padding: '2px 7px', borderRadius: 6, background: '#0a66c2', color: 'var(--ds-text-on-dark)', textDecoration: 'none' }}>in</a>
                        )}
                    </div>
                ) : null}
                {/* SRS #27: policy topics */}
                {a.topics && a.topics.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                        {a.topics.slice(0, 3).map(t => (
                            <span key={t} style={{ fontSize: '0.66rem', fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: isDark ? 'rgba(255,255,255,0.06)' : '#f2f4f7', color: isDark ? '#98a2b3' : '#475467' }}>
                                {t.replace(/_/g, ' ')}
                            </span>
                        ))}
                    </div>
                )}

                {/* Footer */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', paddingTop: 12, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f2f4f7'}` }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: a.author ? getAvatarColor(a.author) : '#667085', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ds-text-on-dark)', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}>
                        {getInitials(a.author || a.source_name || 'N')}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: isDark ? '#d0d5dd' : '#344054', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {a.author || a.source_name || 'Editorial'}
                        </div>
                        {a.published_date && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--ds-text-muted)' }}>{formatDate(a.published_date)}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const SkeletonBlogCard: React.FC<{ isDark: boolean }> = ({ isDark }) => (
    <div style={{ borderRadius: 14, overflow: 'hidden', background: isDark ? '#101828' : 'var(--ds-surface)', boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(16,24,40,0.06)' }}>
        <div style={{ height: 190, background: isDark ? '#1d2939' : 'var(--ds-surface-2)', animation: 'pulse 1.5s ease-in-out infinite' }} />
        <div style={{ padding: '18px 20px 20px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ height: 12, width: '40%', borderRadius: 6, background: isDark ? '#1d2939' : 'var(--ds-surface-2)', animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div style={{ height: 16, width: '90%', borderRadius: 6, background: isDark ? '#1d2939' : 'var(--ds-surface-2)', animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div style={{ height: 14, width: '70%', borderRadius: 6, background: isDark ? '#1d2939' : 'var(--ds-surface-2)', animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: isDark ? '#1d2939' : 'var(--ds-surface-2)', animation: 'pulse 1.5s ease-in-out infinite', flexShrink: 0 }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ height: 10, width: '50%', borderRadius: 4, background: isDark ? '#1d2939' : 'var(--ds-surface-2)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    <div style={{ height: 8, width: '35%', borderRadius: 4, background: isDark ? '#1d2939' : 'var(--ds-surface-2)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                </div>
            </div>
        </div>
    </div>
);

const Newsletters: React.FC = () => {
    const { theme } = useTheme();
    const { user, token } = useAuth();
    const isDark = theme === 'dark';
    const [newsletterArticles, setNewsletterArticles] = useState<dashboardApi.NewsletterArticle[]>([]);
    const [isLoadingNewsletters, setIsLoadingNewsletters] = useState(false);

    // Admins get a per-article delete control so off-topic items that slip past
    // the scraper's relevance filter can be cleared from the feed.
    const isAdmin = React.useMemo(() => {
        try {
            return Boolean(JSON.parse(localStorage.getItem('auth_meta') || '{}')?.is_admin);
        } catch {
            return false;
        }
    }, []);

    const handleDeleteArticle = React.useCallback(async (article: dashboardApi.NewsletterArticle) => {
        if (!token || !article.id) return;
        // eslint-disable-next-line no-restricted-globals
        if (!window.confirm(`Remove this article from the feed?\n\n${article.title}`)) return;
        const previous = article;
        // Optimistic removal — restore the row if the API call fails.
        setNewsletterArticles(prev => prev.filter(x => x.id !== article.id));
        setNewslettersCount(prev => Math.max(0, prev - 1));
        try {
            await adminApi.deleteNewsArticle(token, article.id);
        } catch {
            setNewsletterArticles(prev => [previous, ...prev]);
            setNewslettersCount(prev => prev + 1);
            window.alert('Could not delete this article. Please try again.');
        }
    }, [token]);
    const [newsletterPage, setNewsletterPage] = useState<number>(1);
    const [newslettersCount, setNewslettersCount] = useState<number>(0);
    const [filter24h, setFilter24h] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCountry, setFilterCountry] = useState('');
    const [filterSource, setFilterSource] = useState('');
    const [allCountries, setAllCountries] = useState<string[]>([]);
    const [allSources, setAllSources] = useState<string[]>([]);
    const [bookmarkedUrls, setBookmarkedUrls] = useState<Set<string>>(new Set());
    const [filterBookmarks, setFilterBookmarks] = useState(false);
    const [digestFrequency, setDigestFrequency] = useState<dashboardApi.NewsletterFrequency>('none');
    const [digestDay, setDigestDay] = useState('Monday');
    // SRS #28/#30: personalisation picks + monthly day; options come from the server.
    const [monthlyDay, setMonthlyDay] = useState(1);
    const [picks, setPicks] = useState<{ countries: string[]; topics: string[]; sources: string[]; industries: string[] }>({ countries: [], topics: [], sources: [], industries: [] });
    const [effective, setEffective] = useState<dashboardApi.NewsletterPreference['effective']>(null);
    const [options, setOptions] = useState<dashboardApi.NewsletterOptions | null>(null);
    const [personaliseOpen, setPersonaliseOpen] = useState(false);
    const [filterTopic, setFilterTopic] = useState('');
    const [filterKind, setFilterKind] = useState('');   // SRS #31
    const [mediaKinds, setMediaKinds] = useState<string[]>(['article', 'podcast', 'video', 'linkedin']);
    const [forYou, setForYou] = useState(false);
    const [digestMessage, setDigestMessage] = useState('');
    const [digestLoading, setDigestLoading] = useState(false);
    const [digestSaving, setDigestSaving] = useState(false);
    const [digestSendingNow, setDigestSendingNow] = useState(false);
    const NEWSLETTERS_PAGE_SIZE = 10;

    useEffect(() => {
        const stored = localStorage.getItem('newsletter_bookmarks');
        if (stored) {
            try { setBookmarkedUrls(new Set(JSON.parse(stored))); } catch (e) { /* ignore */ }
        }
    }, []);

    useEffect(() => {
        if (!token) {
            setDigestFrequency('none');
            setDigestDay('Monday');
            return;
        }

        let isMounted = true;
        setDigestLoading(true);
        dashboardApi.getNewsletterPreference(token)
            .then((preference) => {
                if (!isMounted) return;
                const nextFrequency = preference.frequency || (preference.weekly_enabled ? 'weekly' : 'none');
                setDigestFrequency(nextFrequency);
                setDigestDay(preference.preferred_day && WEEKLY_DAYS.includes(preference.preferred_day) ? preference.preferred_day : 'Monday');
                setMonthlyDay(preference.monthly_day || 1);
                setPicks({ countries: preference.countries || [], topics: preference.topics || [], sources: preference.sources || [], industries: preference.industries || [] });
                setEffective(preference.effective || null);
            })
            .catch(() => {
                if (!isMounted) return;
                setDigestMessage('Could not load your weekly email preference right now.');
            })
            .finally(() => {
                if (isMounted) setDigestLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [token]);

    useEffect(() => {
        dashboardApi.getNewsletterOptions().then(setOptions).catch(() => {});
        dashboardApi.getMediaKinds().then(r => setMediaKinds(r.kinds)).catch(() => {});
    }, []);

    const togglePick = (kind: keyof typeof picks, value: string) =>
        setPicks(prev => ({ ...prev, [kind]: prev[kind].includes(value) ? prev[kind].filter(v => v !== value) : [...prev[kind], value] }));

    const toggleBookmark = (url: string) => {
        setBookmarkedUrls(prev => {
            const newSet = new Set(prev);
            if (newSet.has(url)) { newSet.delete(url); } else { newSet.add(url); }
            localStorage.setItem('newsletter_bookmarks', JSON.stringify(Array.from(newSet)));
            return newSet;
        });
    };

    const saveDigestPreference = async (overrideFrequency?: dashboardApi.NewsletterFrequency) => {
        if (!token) {
            setDigestMessage('Please sign in to save your weekly newsletter preference.');
            return;
        }
        // QA fix: selecting a frequency (esp. "Off") must persist to the backend,
        // not just flip the label. Callers can pass the chosen frequency directly
        // to avoid reading stale state right after setDigestFrequency().
        const freq = overrideFrequency ?? digestFrequency;

        setDigestSaving(true);
        setDigestMessage('');
        try {
            const result = await dashboardApi.updateNewsletterPreference(token, {
                frequency: freq,
                preferred_day: digestDay as dashboardApi.NewsletterPreference['preferred_day'],
                monthly_day: monthlyDay,
                ...picks,
            });
            const nextFrequency = result.frequency || (result.weekly_enabled ? 'weekly' : 'none');
            setDigestFrequency(nextFrequency);
            setDigestDay(result.preferred_day || 'Monday');
            setMonthlyDay(result.monthly_day || 1);
            setEffective(result.effective || null);
            setDigestMessage(result.detail || (nextFrequency === 'weekly'
                ? `Weekly newsletter preference saved for ${result.preferred_day}.`
                : nextFrequency === 'daily'
                    ? 'Daily newsletter emails enabled.'
                    : 'Newsletter emails turned off.'));
        } catch (error) {
            setDigestMessage(error instanceof Error ? error.message : 'Unable to save your weekly newsletter preference right now.');
        } finally {
            setDigestSaving(false);
        }
    };

    const sendDigestPreviewNow = async () => {
        if (!token) {
            setDigestMessage('Please sign in to send yourself a preview email.');
            return;
        }

        setDigestSendingNow(true);
        setDigestMessage('');
        try {
            const result = await dashboardApi.sendNewsletterPreviewNow(token);
            setDigestMessage(result.detail || 'A preview newsletter email has been sent. Please check your inbox.');
        } catch (error) {
            setDigestMessage(error instanceof Error ? error.message : 'Unable to send the preview email right now.');
        } finally {
            setDigestSendingNow(false);
        }
    };

    useEffect(() => {
        dashboardApi.getNewslettersFilters()
            .then(data => { setAllCountries(data.countries); setAllSources(data.sources); })
            .catch(() => {});
    }, []);

    useEffect(() => {
        const fetchNewsletters = async () => {
            try {
                setIsLoadingNewsletters(true);
                const offset = (newsletterPage - 1) * NEWSLETTERS_PAGE_SIZE;
                // Filter by country SERVER-SIDE. The feed is sorted by publish date
                // and dominated by high-frequency publishers (e.g. India), so a
                // client-side country filter on a single fetched page hides
                // lower-volume countries (China/UK/etc.) that exist in the DB but
                // aren't on the first page. The endpoint supports `country` (ILIKE).
                const resp = await dashboardApi.getNewsletters({
                    limit: NEWSLETTERS_PAGE_SIZE,
                    offset,
                    country: filterCountry || undefined,
                    topic: filterTopic || undefined,          // SRS #27
                    kind: filterKind || undefined,            // SRS #31
                    personalised: forYou && !!token,          // SRS #28
                });
                if (resp && Array.isArray(resp.articles)) {
                    setNewsletterArticles(resp.articles);
                    setNewslettersCount(resp.total || 0);
                }
            } catch (err) {
                console.error('Error fetching newsletters:', err);
            } finally {
                setIsLoadingNewsletters(false);
            }
        };
        fetchNewsletters();
    }, [newsletterPage, filterCountry, filterTopic, filterKind, forYou, token]);

    // Reset to page 1 whenever a server-side filter changes, so we
    // don't land on an out-of-range page with no results.
    useEffect(() => { setNewsletterPage(1); }, [filterCountry, filterTopic, filterKind, forYou]);

    const cutoff24h = new Date(Date.now() - 24 * 60 * 60 * 1000);

    // Detect image URLs shared by more than one article (generic logos like Google News)
    const repeatedImageUrls = React.useMemo(() => {
        const counts: Record<string, number> = {};
        newsletterArticles.forEach(a => { if (a.image_url) counts[a.image_url] = (counts[a.image_url] ?? 0) + 1; });
        return new Set(Object.keys(counts).filter(url => counts[url] > 1));
    }, [newsletterArticles]);

    const displayedArticles = newsletterArticles.filter(a => {
        if (filterBookmarks && !bookmarkedUrls.has(a.url)) return false;
        // "Last 24h" = ingested in the last 24h (scraped_at). RSS-sourced articles
        // often carry an older published_date, so filtering on publish date would
        // hide freshly-ingested foreign news. Fall back to published_date if unset.
        if (filter24h) {
            const recencyTs = a.scraped_at || a.published_date;
            if (!(recencyTs && new Date(recencyTs) >= cutoff24h)) return false;
        }
        if (searchQuery && !a.title?.toLowerCase().includes(searchQuery.toLowerCase()) && !a.source_name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        if (filterCountry && a.country !== filterCountry) return false;
        if (filterSource && a.source_name !== filterSource) return false;
        return true;
    });


    const activeFilters: { label: string; clear: () => void }[] = [];
    if (filterCountry) activeFilters.push({ label: filterCountry, clear: () => setFilterCountry('') });
    if (filterSource) activeFilters.push({ label: filterSource, clear: () => setFilterSource('') });
    if (filterTopic) activeFilters.push({ label: options?.topics.find(t => t.key === filterTopic)?.label || filterTopic, clear: () => setFilterTopic('') });
    if (forYou) activeFilters.push({ label: 'For you', clear: () => setForYou(false) });
    if (filterKind) activeFilters.push({ label: KIND_LABEL[filterKind] || filterKind, clear: () => setFilterKind('') });
    if (filter24h) activeFilters.push({ label: 'Last 24h', clear: () => setFilter24h(false) });
    if (filterBookmarks) activeFilters.push({ label: 'Bookmarked', clear: () => setFilterBookmarks(false) });

    const totalPages = Math.max(1, Math.ceil((newslettersCount || 0) / NEWSLETTERS_PAGE_SIZE));

    const filterBtnStyle = (active: boolean): React.CSSProperties => ({
        padding: '8px 16px', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
        border: `1.5px solid ${active ? ACCENT : (isDark ? 'rgba(255,255,255,0.15)' : '#eaecf0')}`,
        background: active ? (isDark ? 'rgba(97, 114, 243, 0.15)' : ACCENT_LIGHT) : (isDark ? '#101828' : '#ffffff'),
        color: active ? (isDark ? '#c7d7fe' : ACCENT) : (isDark ? '#98a2b3' : '#667085'),
        transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6,
    });

    return (
        <div className="nl-page">
            <style>{`
                @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.45} }
                .nl-page { padding: 0.75rem 0.25rem 3rem 0.25rem; width: 100%; box-sizing: border-box; }
                .nl-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr)); gap: 24px; }
                @media (max-width: 900px) {
                    .nl-page { padding: 1.25rem 1.25rem 2rem 1.25rem; }
                    .nl-grid { gap: 16px; }
                }
                @media (max-width: 600px) {
                    .nl-page { padding: 1rem 0.75rem 1.5rem 0.75rem; }
                    .nl-grid { grid-template-columns: 1fr; gap: 12px; }
                }
            `}</style>

            {/* Page header */}
            <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                    <span style={{ display: 'inline-block', width: 3, height: 18, borderRadius: 4, background: ACCENT }} />
                    <h6 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: isDark ? '#f9fafb' : '#101828', letterSpacing: '-0.01em' }}>AI Policy Newsletter</h6>
                </div>
                
            </div>

            <div style={{
                marginBottom: 16,
                padding: '14px 16px',
                borderRadius: 12,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#eaecf0'}`,
                background: isDark ? '#101828' : 'var(--ds-surface)',
                boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 10px rgba(16,24,40,0.05)',
            }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: isDark ? '#f9fafb' : '#101828', marginBottom: 4 }}>
                            Newsletter digest
                        </div>
                        <div style={{ fontSize: '0.82rem', lineHeight: 1.6, color: isDark ? '#98a2b3' : '#667085' }}>
                            Choose a <strong>daily</strong>, <strong>weekly</strong> or <strong>monthly</strong> digest{user?.email ? ` for ${user.email}` : ''}, built from the countries, topics and sources you pick below.
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {([
                            { value: 'daily', label: 'Daily' },
                            { value: 'weekly', label: 'Weekly' },
                            { value: 'monthly', label: 'Monthly' },
                            { value: 'none', label: 'Off' },
                        ] as const).filter(o => o.value === 'none' || !options || options.frequencies.includes(o.value)).map((option) => {
                            const active = digestFrequency === option.value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    disabled={!token || digestLoading || digestSaving || digestSendingNow}
                                    onClick={() => { setDigestFrequency(option.value); saveDigestPreference(option.value); }}
                                    style={{
                                        padding: '8px 12px',
                                        borderRadius: 999,
                                        border: `1px solid ${active ? ACCENT : (isDark ? 'rgba(255,255,255,0.14)' : '#d0d5dd')}`,
                                        background: active ? (isDark ? 'rgba(97, 114, 243, 0.16)' : ACCENT_LIGHT) : (isDark ? '#101828' : '#ffffff'),
                                        color: active ? (isDark ? '#c7d7fe' : ACCENT) : (isDark ? '#eaecf0' : '#344054'),
                                        fontSize: '0.82rem',
                                        fontWeight: 700,
                                        cursor: !token || digestLoading || digestSaving || digestSendingNow ? 'not-allowed' : 'pointer',
                                        opacity: !token || digestLoading || digestSaving || digestSendingNow ? 0.7 : 1,
                                    }}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 12 }}>
                    {digestFrequency === 'weekly' && (
                        <select
                            value={digestDay}
                            onChange={(e) => setDigestDay(e.target.value)}
                            disabled={!token || digestLoading || digestSaving || digestSendingNow}
                            style={{
                                padding: '8px 12px',
                                borderRadius: 8,
                                fontSize: '0.85rem',
                                border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#eaecf0'}`,
                                background: isDark ? '#101828' : 'var(--ds-surface)',
                                color: isDark ? '#eaecf0' : '#344054',
                                cursor: !token || digestLoading || digestSaving || digestSendingNow ? 'not-allowed' : 'pointer',
                                opacity: !token || digestLoading || digestSaving || digestSendingNow ? 0.6 : 1,
                            }}
                        >
                            {WEEKLY_DAYS.map(day => <option key={day} value={day}>{day}</option>)}
                        </select>
                    )}
                    {digestFrequency === 'monthly' && (
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: isDark ? '#d0d5dd' : '#475467' }}>
                            Day of month
                            <select
                                value={monthlyDay}
                                onChange={(e) => setMonthlyDay(Number(e.target.value))}
                                disabled={!token || digestLoading || digestSaving || digestSendingNow}
                                aria-label="Day of month"
                                style={{ padding: '8px 12px', borderRadius: 8, fontSize: '0.85rem', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#eaecf0'}`, background: isDark ? '#101828' : 'var(--ds-surface)', color: isDark ? '#eaecf0' : '#344054' }}
                            >
                                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </label>
                    )}
                    <button
                        type="button"
                        onClick={() => setPersonaliseOpen(o => !o)}
                        disabled={!token}
                        aria-expanded={personaliseOpen}
                        style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#d0d5dd'}`, background: 'transparent', color: isDark ? '#f9fafb' : '#101828', fontSize: '0.84rem', fontWeight: 700, cursor: token ? 'pointer' : 'not-allowed', opacity: token ? 1 : 0.7 }}
                    >
                        Personalise{(picks.countries.length + picks.topics.length + picks.sources.length + picks.industries.length) > 0 ? ` (${picks.countries.length + picks.topics.length + picks.sources.length + picks.industries.length})` : ''}
                    </button>

                    <button
                        type="button"
                        onClick={() => saveDigestPreference()}
                        disabled={!token || digestLoading || digestSaving || digestSendingNow}
                        style={{
                            padding: '8px 14px',
                            borderRadius: 8,
                            border: 'none',
                            background: digestFrequency === 'none' ? '#344054' : ACCENT,
                            color: 'var(--ds-text-on-dark)',
                            fontSize: '0.84rem',
                            fontWeight: 700,
                            cursor: !token || digestLoading || digestSaving || digestSendingNow ? 'not-allowed' : 'pointer',
                            opacity: !token || digestLoading || digestSaving || digestSendingNow ? 0.7 : 1,
                        }}
                    >
                        {digestSaving ? 'Saving...' : (digestFrequency === 'none' ? 'Turn off emails' : 'Save preference')}
                    </button>

                    <button
                        type="button"
                        onClick={sendDigestPreviewNow}
                        disabled={!token || digestLoading || digestSaving || digestSendingNow}
                        style={{
                            padding: '8px 14px',
                            borderRadius: 8,
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : '#d0d5dd'}`,
                            background: isDark ? '#1d2939' : 'var(--ds-surface-2)',
                            color: isDark ? '#f9fafb' : '#101828',
                            fontSize: '0.84rem',
                            fontWeight: 700,
                            cursor: !token || digestLoading || digestSaving || digestSendingNow ? 'not-allowed' : 'pointer',
                            opacity: !token || digestLoading || digestSaving || digestSendingNow ? 0.7 : 1,
                        }}
                    >
                        {digestSendingNow ? 'Sending...' : 'Send now'}
                    </button>

                    {digestLoading && (
                        <span style={{ fontSize: '0.8rem', color: isDark ? '#d0d5dd' : '#475467' }}>
                            Loading saved preference...
                        </span>
                    )}
                    {!digestLoading && digestMessage && (
                        <span style={{ fontSize: '0.8rem', color: isDark ? '#d0d5dd' : '#475467' }}>
                            {digestMessage}
                        </span>
                    )}
                </div>

                {personaliseOpen && options && (
                    <div data-testid="personalise-panel" style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#eaecf0'}`, display: 'grid', gap: 12 }}>
                        {([
                            { kind: 'countries', label: 'Countries & regions', items: options.countries.map(c => ({ value: c, label: c.toUpperCase() === c ? c : c.replace(/\b\w/g, ch => ch.toUpperCase()) })) },
                            { kind: 'topics', label: 'Topics', items: options.topics.map(t => ({ value: t.key, label: t.label })) },
                            { kind: 'industries', label: 'Industry', items: options.industries.map(i => ({ value: i, label: i })) },
                            { kind: 'sources', label: 'Sources', items: options.sources.map(src => ({ value: src.source_key, label: src.name })) },
                        ] as const).map(group => (
                            <div key={group.kind}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: isDark ? '#98a2b3' : '#667085', marginBottom: 6 }}>{group.label}</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {group.items.map(item => {
                                        const on = picks[group.kind].includes(item.value);
                                        return (
                                            <button key={item.value} type="button" onClick={() => togglePick(group.kind, item.value)} aria-pressed={on}
                                                style={{ padding: '4px 10px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', border: `1px solid ${on ? ACCENT : (isDark ? 'rgba(255,255,255,0.14)' : '#d0d5dd')}`, background: on ? (isDark ? 'rgba(97, 114, 243, 0.16)' : ACCENT_LIGHT) : 'transparent', color: on ? (isDark ? '#c7d7fe' : ACCENT) : (isDark ? '#eaecf0' : '#344054') }}>
                                                {item.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                        <div style={{ fontSize: '0.78rem', color: isDark ? '#98a2b3' : '#667085' }}>
                            {effective && !effective.explicit
                                ? <>Nothing picked yet — your digest follows your profile ({[...effective.countries, ...effective.industries].join(', ') || 'no country or industry set'}). Pick anything above to override.</>
                                : <>Your digest leads with what matches these picks and tops up with the latest policy news.</>}
                            {' '}Click <strong>Save preference</strong> to apply.
                        </div>
                    </div>
                )}
            </div>

            {/* Filter bar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12, alignItems: 'center' }}>
                {/* Search */}
                <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180, maxWidth: 320 }}>
                    <svg style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#667085' : '#98a2b3'} strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input
                        type="text"
                        placeholder="Search articles..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        style={{ width: '100%', paddingLeft: 33, paddingRight: 10, paddingTop: 8, paddingBottom: 8, borderRadius: 8, fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#eaecf0'}`, background: isDark ? '#101828' : 'var(--ds-surface)', color: isDark ? '#f2f4f7' : '#101828' }}
                    />
                </div>

                {/* Country */}
                <div style={{ position: 'relative' }}>
                    <svg style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#667085' : '#98a2b3'} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                    <select value={filterCountry} onChange={e => setFilterCountry(e.target.value)} style={{ paddingLeft: 26, paddingRight: 10, paddingTop: 8, paddingBottom: 8, borderRadius: 8, fontSize: '0.875rem', cursor: 'pointer', outline: 'none', border: `1.5px solid ${filterCountry ? ACCENT : (isDark ? 'rgba(255,255,255,0.12)' : '#eaecf0')}`, background: isDark ? '#101828' : 'var(--ds-surface)', color: filterCountry ? ACCENT : (isDark ? '#98a2b3' : '#667085'), appearance: 'none' }}>
                        <option value="">All Countries</option>
                        {allCountries.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                {/* Source */}
                <div style={{ position: 'relative' }}>
                    <svg style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#667085' : '#98a2b3'} strokeWidth="2"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2z"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8z"/></svg>
                    <select value={filterSource} onChange={e => setFilterSource(e.target.value)} style={{ paddingLeft: 26, paddingRight: 10, paddingTop: 8, paddingBottom: 8, borderRadius: 8, fontSize: '0.875rem', cursor: 'pointer', outline: 'none', border: `1.5px solid ${filterSource ? ACCENT : (isDark ? 'rgba(255,255,255,0.12)' : '#eaecf0')}`, background: isDark ? '#101828' : 'var(--ds-surface)', color: filterSource ? ACCENT : (isDark ? '#98a2b3' : '#667085'), appearance: 'none', maxWidth: 190 }}>
                        <option value="">All Sources</option>
                        {allSources.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>

                {/* Media kind (SRS #31) */}
                {mediaKinds.length > 1 && (
                    <div role="group" aria-label="Media kind" style={{ display: 'flex', gap: 4 }}>
                        {mediaKinds.map(k => (
                            <button key={k} type="button" onClick={() => setFilterKind(filterKind === k ? '' : k)} aria-pressed={filterKind === k} style={filterBtnStyle(filterKind === k)}>
                                {KIND_ICON[k]} {KIND_LABEL[k] || k}
                            </button>
                        ))}
                    </div>
                )}

                {/* Topic (SRS #27) */}
                <select value={filterTopic} onChange={e => setFilterTopic(e.target.value)} aria-label="Topic" style={{ paddingLeft: 10, paddingRight: 10, paddingTop: 8, paddingBottom: 8, borderRadius: 8, fontSize: '0.875rem', cursor: 'pointer', outline: 'none', border: `1.5px solid ${filterTopic ? ACCENT : (isDark ? 'rgba(255,255,255,0.12)' : '#eaecf0')}`, background: isDark ? '#101828' : 'var(--ds-surface)', color: filterTopic ? ACCENT : (isDark ? '#98a2b3' : '#667085'), appearance: 'none', maxWidth: 220 }}>
                    <option value="">All Topics</option>
                    {(options?.topics ?? []).map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>

                {token && (
                    <button onClick={() => setForYou(f => !f)} style={filterBtnStyle(forYou)} title="Order by your newsletter preferences (SRS #28)">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l2.9 6.6L22 9.3l-5.5 4.8L18.2 21 12 17.3 5.8 21l1.7-6.9L2 9.3l7.1-.7z"/></svg>
                        For you
                    </button>
                )}

                <button onClick={() => setFilter24h(f => !f)} style={filterBtnStyle(filter24h)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    Last 24h
                </button>

                <button onClick={() => setFilterBookmarks(f => !f)} style={filterBtnStyle(filterBookmarks)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill={filterBookmarks ? ACCENT : 'none'} stroke={filterBookmarks ? ACCENT : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                    </svg>
                    Bookmarks ({bookmarkedUrls.size})
                </button>
            </div>

            {/* Active filter chips */}
            {activeFilters.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20, alignItems: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--ds-text-muted)' }}>Active:</span>
                    {activeFilters.map(f => (
                        <span key={f.label} onClick={f.clear} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, fontSize: '0.73rem', fontWeight: 700, background: isDark ? 'rgba(97, 114, 243, 0.15)' : ACCENT_LIGHT, color: isDark ? '#c7d7fe' : ACCENT, border: `1px solid ${isDark ? 'rgba(97, 114, 243, 0.3)' : '#e0eaff'}`, cursor: 'pointer' }}>
                            {f.label}
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </span>
                    ))}
                    <span onClick={() => { setFilterCountry(''); setFilterSource(''); setFilterTopic(''); setFilterKind(''); setForYou(false); setFilter24h(false); setFilterBookmarks(false); setSearchQuery(''); }} style={{ fontSize: '0.75rem', color: 'var(--ds-text-muted)', cursor: 'pointer', textDecoration: 'underline', marginLeft: 4 }}>Clear all</span>
                </div>
            )}

            {isLoadingNewsletters ? (
                <div className="nl-grid">
                    {Array.from({ length: 9 }).map((_, i) => <SkeletonBlogCard key={i} isDark={isDark} />)}
                </div>
            ) : displayedArticles.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '5rem 2rem', textAlign: 'center', borderRadius: 16, border: `2px dashed ${isDark ? '#344054' : '#eaecf0'}` }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: isDark ? '#1d2939' : ACCENT_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#667085' : ACCENT} strokeWidth="1.5"><path d="M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10l6 6v8a2 2 0 0 1-2 2z" strokeLinecap="round" strokeLinejoin="round"/><polyline points="17 21 17 13 7 13 7 21" strokeLinecap="round" strokeLinejoin="round"/><polyline points="7 3 7 8 15 8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: 700, color: isDark ? '#eaecf0' : '#1d2939' }}>No articles found</h3>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--ds-text-muted)' }}>Try adjusting your filters or search query.</p>
                </div>
            ) : (
                <>
                    {/* 3-column grid */}
                    <div className="nl-grid">
                        {displayedArticles.map((a, idx) => (
                            <BlogCard
                                key={idx}
                                a={a}
                                isDark={isDark}
                                isBookmarked={bookmarkedUrls.has(a.url)}
                                onBookmarkToggle={toggleBookmark}
                                placeholderIdx={idx}
                                repeatedImageUrls={repeatedImageUrls}
                                onDelete={isAdmin ? handleDeleteArticle : undefined}
                            />
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (() => {
                        const pageNums: (number | null)[] = totalPages <= 7
                            ? Array.from({ length: totalPages }, (_, i) => i + 1)
                            : newsletterPage <= 4 ? [1,2,3,4,5,null,totalPages]
                            : newsletterPage >= totalPages-3 ? [1,null,totalPages-4,totalPages-3,totalPages-2,totalPages-1,totalPages]
                            : [1,null,newsletterPage-1,newsletterPage,newsletterPage+1,null,totalPages];
                        return (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 40, paddingTop: 24, borderTop: `1px solid ${isDark ? '#1d2939' : '#f2f4f7'}`, flexWrap: 'wrap' }}>
                                <button disabled={newsletterPage <= 1} onClick={() => setNewsletterPage(p => Math.max(1, p-1))} style={{ padding: '8px 16px', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#eaecf0'}`, background: isDark ? '#101828' : 'var(--ds-surface)', color: isDark ? '#98a2b3' : '#475467', cursor: newsletterPage <= 1 ? 'not-allowed' : 'pointer', opacity: newsletterPage <= 1 ? 0.4 : 1 }}>← Prev</button>
                                {pageNums.map((page, idx) => page === null
                                    ? <span key={`e${idx}`} style={{ padding: '0 4px', color: 'var(--ds-text-muted)', alignSelf: 'center' }}>…</span>
                                    : <button key={page} onClick={() => setNewsletterPage(page)} style={{ padding: '8px 14px', borderRadius: 8, fontSize: '0.85rem', fontWeight: page === newsletterPage ? 800 : 600, border: `1.5px solid ${page === newsletterPage ? '#344054' : (isDark ? 'rgba(255,255,255,0.15)' : '#eaecf0')}`, background: page === newsletterPage ? '#344054' : (isDark ? '#101828' : 'var(--ds-surface)'), color: page === newsletterPage ? 'var(--ds-text-on-dark)' : (isDark ? '#98a2b3' : '#475467'), cursor: 'pointer', transition: 'all 0.15s', minWidth: 40 }}>{page}</button>
                                )}
                                <button disabled={newsletterPage >= totalPages} onClick={() => setNewsletterPage(p => p+1)} style={{ padding: '8px 16px', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#eaecf0'}`, background: isDark ? '#101828' : 'var(--ds-surface)', color: isDark ? '#98a2b3' : '#475467', cursor: newsletterPage >= totalPages ? 'not-allowed' : 'pointer', opacity: newsletterPage >= totalPages ? 0.4 : 1 }}>Next →</button>
                            </div>
                        );
                    })()}
                </>
            )}
        </div>
    );
};

export default Newsletters;
